import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { loadPowerPlantIcons } from '../../utils/structures'
import { powerPlantsToGeoJSON } from '../../utils/powerPlants'
import { useMapStore, mapState } from '../../store/mapStore'

// Country-owned power plants share the Structures toggle: both are gameplay
// buildings, and keeping one switch avoids a hidden construction result.
export function usePowerPlants(map, { showStructures, constructionMode, infrastructureMode, powerPlantsData, regionsData, onPowerPlantMove }) {
  // MapLibre event callbacks are registered once. Keep their marker source
  // current across React renders so a drag starts from latest plant state.
  const latestGeoJSON = useRef({ type: 'FeatureCollection', features: [] })

  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill') || !regionsData) return

    const visible = (!!showStructures || !!constructionMode) && !infrastructureMode
    loadPowerPlantIcons(map.current, powerPlantsData)
    const fc = powerPlantsToGeoJSON(visible ? powerPlantsData : [], regionsData)
    latestGeoJSON.current = fc
    const src = map.current.getSource('power-plants')
    if (!src) {
      map.current.addSource('power-plants', { type: 'geojson', data: fc })
      map.current.addLayer({
        id: 'power-plants-points',
        type: 'symbol',
        source: 'power-plants',
        layout: {
          'icon-image': ['concat', 'power-plant-icon-', ['get', 'icon_key']],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 2, 0.10, 5, 0.24, 8, 0.42, 10, 0.58],
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
      })

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'region-hover-popup structure-hover-popup',
      })
      map.current.on('mousemove', 'power-plants-points', (event) => {
        const p = event.features?.[0]?.properties
        if (!p) return
        const state = p.is_operational === false || p.is_operational === 'false'
          ? `En construction${p.construction_end_date ? ` — mise en service ${p.construction_end_date}` : ''}`
          : 'En service'
        popup.setLngLat(event.lngLat).setHTML(`
          <div style="font-size:12px;padding:4px;">
            <strong>⚡ ${p.type} niv.${p.level}</strong><br/>
            ${Number(p.quantity || 1).toLocaleString('fr-FR')} unité(s) · ${Number(p.production_mwh || 0).toLocaleString('fr-FR')} MWh<br/>
            ${state} · ${p.region_name || `Région #${p.region_id}`}
          </div>
        `).addTo(map.current)
        map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', 'power-plants-points', () => {
        popup.remove()
        map.current.getCanvas().style.cursor = ''
      })
      map.current.on('mousedown', 'power-plants-points', (event) => {
        if (!mapState().constructionModeActive || !onPowerPlantMove || event.originalEvent.button !== 0) return
        const plant = event.features?.[0]?.properties
        if (!plant) return
        event.preventDefault()
        useMapStore.setState({ structDragActive: true })
        popup.remove()
        map.current.getCanvas().style.cursor = 'grabbing'
        const source = map.current.getSource('power-plants')
        const dragData = structuredClone(latestGeoJSON.current)
        const move = (moveEvent) => {
          const feature = dragData.features.find((item) => Number(item.properties.id) === Number(plant.id))
          if (feature) feature.geometry.coordinates = [moveEvent.lngLat.lng, moveEvent.lngLat.lat]
          source.setData(dragData)
        }
        const up = (upEvent) => {
          map.current.off('mousemove', move)
          useMapStore.setState({ structDragActive: false, suppressNextMapClick: Date.now() })
          map.current.getCanvas().style.cursor = 'crosshair'
          source.setData(latestGeoJSON.current)
          const region = map.current.queryRenderedFeatures(upEvent.point, { layers: ['regions-fill'] })[0]?.properties || null
          onPowerPlantMove(plant, upEvent.lngLat, region)
        }
        map.current.on('mousemove', move)
        map.current.once('mouseup', up)
      })
    } else {
      src.setData(fc)
    }
    if (map.current.getLayer('power-plants-points')) {
      map.current.setLayoutProperty('power-plants-points', 'visibility', visible ? 'visible' : 'none')
    }
  }, [map, showStructures, constructionMode, infrastructureMode, powerPlantsData, regionsData, onPowerPlantMove])
}
